import { Box, Flex, Grid, List, ListItem, Stack, Text } from '@chakra-ui/react';
import { Badge } from '@foam/buoy/src/components/Badge/Badge';
import { noop } from 'lodash';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useState } from 'react';
import { useSelector } from 'react-redux';
import { match } from 'ts-pattern';
import {
  ChevronRightIcon,
  CogwheelIcon,
  LinkIcon,
  Paragraph,
  PictureIcon,
  PlatformInstagramBorderIcon,
  PlatformTikTokColorIcon,
  PlatformYouTubeColorIcon,
  ReelIcon,
  TextBoldIcon,
  Tooltip,
} from 'ui-components';
import authApi from '~/authApi';
import {
  FEATURE_FLAGS,
  SOCIAL_PLATFORMS,
  SOCIAL_PLATFORMS_NAMES,
  USER_ROLES,
  USER_ROLES_API_MGMNT,
} from '~/constants';
import { MEDIA_KIT_TEXTS } from '~/constants/mediaKitTexts';
import { useGetMediaKit } from '~/hooks/api/media-kits/get';
import { useFeature } from '~/hooks/useFeatureFlag/useFeature';
import { selectTalentManager } from '~/selectors/talent-manager';
import customTheme from '~/styles/theme';
import { useCurrentUserAccess } from '~/userManagementApi/userAccess';
import { MeResponse } from '~/userManagementApi/userAccess.types';
import { getMainRoleUserMgnmtApi } from '~/userManagementApi/utils/getMainRoleUserMgnmtApi';

import { ContentCard, PlatformContent } from './PlatformContent';
import { usePlatformAnalyticsStore } from './modals/PlatformAnalytics/usePlatformAnalyticsStore';
import { SideBarTextBox } from './sidebar/SideBarTextBox';
import { SideBarVideo } from './sidebar/SideBarVideo';
import { useDropActions } from './sidebar/useDropActions';

const platforms = [
  SOCIAL_PLATFORMS.instagram,
  SOCIAL_PLATFORMS.tiktok,
  SOCIAL_PLATFORMS.youtube,
];

const contentTypes = [
  { icon: TextBoldIcon, name: 'Text' },
  { icon: PictureIcon, name: 'Image' },
  { icon: ReelIcon, name: 'Video' },
  { icon: LinkIcon, name: 'URL' },
];

export const getPlatformIcon = (platform: SOCIAL_PLATFORMS): any => {
  const platformIcon = () =>
    match(platform)
      .with(SOCIAL_PLATFORMS.instagram, () => (
        <PlatformInstagramBorderIcon boxSize={4} />
      ))
      .with(SOCIAL_PLATFORMS.tiktok, () => (
        <PlatformTikTokColorIcon boxSize={4} />
      ))
      .with(SOCIAL_PLATFORMS.youtube, () => (
        <PlatformYouTubeColorIcon boxSize={4} />
      ))
      .otherwise(() => undefined);

  return platformIcon;
};

export const Sidebar = () => {
  const { isEnabled: isTextEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsTextbox,
  );
  const { isEnabled: isVideoEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsVideo,
  );
  const { isEnabled: isContentEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsContent,
  );
  const { isEnabled: isStyleEnabled } = useFeature(
    FEATURE_FLAGS.foamMediaKitsStyleBtn,
  );
  const { isEnabled: isPrimaryMediaKitEnabled } = useFeature(
    FEATURE_FLAGS.foamPrimaryMediaKit,
  );

  const router = useRouter();
  const { id: mediaKitId } = router.query;
  const mediaKit = useGetMediaKit({ id: mediaKitId as string });

  const [showTooltip, setShowTooltip] = useState(false);

  const userisCreator = authApi.user.roles.includes(USER_ROLES.creator);

  const manager = useSelector(selectTalentManager);
  const userAccessData = useCurrentUserAccess({
    entityId: manager?.agency?.id,
  });
  const mainRole = getMainRoleUserMgnmtApi(userAccessData.data as MeResponse);
  const isAgencyAdmin = mainRole === USER_ROLES_API_MGMNT.agencyAdmin;

  return (
    <Flex
      flexDirection="column"
      gap={10}
      justifyContent="space-between"
      h="100%"
      position="relative"
      overflow="visible"
    >
      <Flex flexDirection="column" gap={4}>
        {mediaKit.data?.is_public ? (
          <div>
            <Tooltip
              label={
                isPrimaryMediaKitEnabled
                  ? MEDIA_KIT_TEXTS.PRIMARY_KIT_TOOLTIP
                  : MEDIA_KIT_TEXTS.DEFAULT_KIT_TOOLTIP
              }
              placement="top"
            >
              <Badge
                size="sm"
                variant={isPrimaryMediaKitEnabled ? 'secondary' : 'info'}
                icon="Info"
              >
                {isPrimaryMediaKitEnabled
                  ? MEDIA_KIT_TEXTS.PRIMARY
                  : MEDIA_KIT_TEXTS.DEFAULT}
              </Badge>
            </Tooltip>
          </div>
        ) : null}
        <Flex flexDirection="column" gap={1}>
          <Text fontSize="2xl" fontWeight="500" color="neutral.900">
            Edit Media Kit
          </Text>
        </Flex>
        <Box
          backgroundColor="neutral.100"
          border={`1px solid ${customTheme.colors.primary['100']}`}
          borderRadius={8}
        >
          <Stack spacing={4}>
            <Stack direction="column" gap={1} paddingX={4} paddingTop={4}>
              <Text fontSize="md" fontWeight="500" color="neutral.900">
                Platform analytics
              </Text>
              <Text fontSize="sm" fontWeight="400" color="primary.400">
                Summary of their audience demographics and detailed metrics.
              </Text>
            </Stack>

            <List spacing={1} paddingX={2}>
              {platforms.map((platform) => (
                <PlatformListItem key={platform} platform={platform} />
              ))}
            </List>
          </Stack>
        </Box>

        <PlatformContent />
        <SideBarTextBox />
        <SideBarVideo />

        <Text fontSize="2xl" fontWeight="500" color="primary.500">
          Coming Soon
        </Text>
        <Grid fontSize="md" fontWeight="500" color="neutral.900" gap="10px">
          {!isContentEnabled ? (
            <ContentCard isFFEnabled={false} onOpen={noop} />
          ) : null}

          {contentTypes
            .filter((t) => {
              if (t.name === 'Text' && isTextEnabled) return false;
              if (t.name === 'Video' && isVideoEnabled) return false;
              return true;
            })
            .map(({ icon: Icon, name }, index) => (
              <Box
                key={index}
                backgroundColor="neutral.100"
                border={`1px solid ${customTheme.colors.primary['100']}`}
                padding={4}
                borderRadius={8}
                opacity="50%"
              >
                <Flex gap={1} align="center" justify="center">
                  <Icon boxSize="14px" color="primary.700" />
                  <Text>{name}</Text>
                </Flex>
              </Box>
            ))}
        </Grid>
      </Flex>

      {isStyleEnabled && !userisCreator ? (
        <Tooltip
          label={
            isAgencyAdmin
              ? 'Go to Agency Settings'
              : 'Admins only—contact your Admin to customize'
          }
          isOpen={showTooltip}
        >
          <Box
            onMouseEnter={() => setShowTooltip(true)}
            onMouseLeave={() => setShowTooltip(false)}
          >
            {isAgencyAdmin ? (
              <Link
                href="/agency/settings#agency-branding"
                target="_blank"
                track-id="media-kit-builder-sidebar--style-btn"
                data-testid="media-kit-builder-sidebar--style-btn"
              >
                <StyleButton />
              </Link>
            ) : (
              <StyleButton />
            )}
          </Box>
        </Tooltip>
      ) : null}
    </Flex>
  );
};

const StyleButton = () => {
  return (
    <Flex
      border="1px solid"
      borderColor="primary.100"
      rounded={8}
      p={4}
      alignItems="center"
      justifyContent="center"
      gap={1}
    >
      <CogwheelIcon boxSize={4} />
      <Paragraph size="md" fontWeight="medium" color="primary.700">
        Style
      </Paragraph>
    </Flex>
  );
};

const PlatformListItem = ({ platform }: { platform: SOCIAL_PLATFORMS }) => {
  const { setPlatform: setSelectedPlatform } = usePlatformAnalyticsStore();
  const triggerActions = useDropActions();

  const handleAddCharts = () => {
    setSelectedPlatform(platform);
    triggerActions('TalentCharts', {
      platform,
    });
  };

  const Icon = getPlatformIcon(platform);
  const title = SOCIAL_PLATFORMS_NAMES[platform];

  return (
    <ListItem
      as="button"
      key={platform}
      marginY={2}
      padding={2}
      borderRadius={6}
      onClick={handleAddCharts}
      track-id={`add-platform-data-to-media-kit-${platform}`}
      data-testid={`add-platform-data-to-media-kit-${platform}`}
      _hover={{
        cursor: 'pointer',
        background: customTheme.colors.primary['100'],
      }}
      _pressed={{
        background: customTheme.colors.primary['100'],
      }}
    >
      <Flex justify="space-between" align="center">
        <Flex align="center" gap={2}>
          <Icon />
          <Text fontSize="md" fontWeight="400" color="primary.700">
            {title}
          </Text>
        </Flex>
        <ChevronRightIcon boxSize={4} color="primary.300" />
      </Flex>
    </ListItem>
  );
};
